import React from 'react';
import { FormControl, MenuItem, Select } from '@mui/material';
import { styled } from '@mui/material/styles';

// 비-react-hook-form 환경의 select 공용 컴포넌트.
// react-hook-form 환경에선 FormInputDropdown을 사용한다.
//
// Props:
// - value, onChange:    제어된 select의 값/핸들러 (useState 기반)
// - options:            { value, label }[] — label은 string 또는 ReactNode
// - emptyLabel:         "전체" 같은 빈 값 옵션 라벨. undefined이면 빈 옵션 미생성
// - renderOption(opt):  라벨이 단순 텍스트가 아니거나 동적 포맷이 필요한 경우 커스텀 렌더
// - width:              FormControl의 가로 폭 (기본 250)
// - size:               'small' | 'medium' (기본 'small')
// - 그 외 MUI Select props는 그대로 전달
//
// 사용 예:
//   <CommonSelect
//     value={ticketFilter}
//     onChange={handleTicketFilterChange}
//     options={tickets.map(t => ({ value: t.id, label: `${t.name} (${t.status})` }))}
//     emptyLabel="전체"
//   />

const StyledSelect = styled(Select)({
  borderRadius: '8px',
  fontSize: '13px',
  backgroundColor: '#fff',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e0e0e0',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#4AB3BC',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#4AB3BC',
    borderWidth: '1.5px',
  },
  '& .MuiSelect-select': {
    padding: '8px 12px',
  },
  '& .MuiSelect-icon': {
    color: '#aaa',
    right: '8px',
  },
});

const StyledMenuItem = styled(MenuItem)({
  fontSize: '13px',
  padding: '7px 14px',
  borderRadius: '4px',
  margin: '1px 4px',
  transition: 'background-color 0.15s ease',
  '&:hover': {
    backgroundColor: '#f0f8f8',
  },
  '&.Mui-selected': {
    backgroundColor: '#e8f5f5',
    color: '#4AB3BC',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#ddf0f0',
    },
  },
});

const menuProps = {
  PaperProps: {
    sx: {
      borderRadius: '10px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      border: '1px solid #f0f0f0',
      marginTop: '4px',
      maxHeight: '240px',
      '&::-webkit-scrollbar': { width: '4px' },
      '&::-webkit-scrollbar-thumb': { backgroundColor: '#ddd', borderRadius: '4px' },
    },
  },
};

const CommonSelect = ({
  value,
  onChange,
  options = [],
  emptyLabel,
  renderOption,
  width = 250,
  size = 'small',
  disabled,
  ...rest
}) => (
  <FormControl size={size} sx={{ width }}>
    <StyledSelect value={value} onChange={onChange} displayEmpty disabled={disabled} MenuProps={menuProps} {...rest}>
      {emptyLabel !== undefined && <StyledMenuItem value="">{emptyLabel}</StyledMenuItem>}
      {options.map(opt => (
        <StyledMenuItem key={opt.value} value={opt.value}>
          {renderOption ? renderOption(opt) : opt.label}
        </StyledMenuItem>
      ))}
    </StyledSelect>
  </FormControl>
);

export default CommonSelect;
